/** @format */
import { Inject, Injectable } from '@angular/core';
import { StateService, UIRouterGlobals } from '@uirouter/core';
import { CurriculumPeriod, Education, LocalizedString, OtmId, Plan } from 'common-typescript/types';
import {
    combineLatestWith,
    distinctUntilChanged,
    MonoTypeOperatorFunction,
    Observable,
    of,
    shareReplay,
    ShareReplayConfig,
    switchMap,
} from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { AppErrorHandler } from 'sis-components/error-handler/app-error-handler';
import { CurriculumPeriodEntityService } from 'sis-components/service/curriculum-period-entity.service';
import { EducationEntityService } from 'sis-components/service/education-entity.service';
import { setsAreEqual } from 'sis-components/util/utils';
import { PLAN_SEEKER } from '../../../ajs-upgraded-modules';
import { MyPlanService } from '../../service/my-plan.service';
import { PlanOption, PlanSelectService } from '../../service/plan-select.service';

@Injectable()
export class MyPlanSelectComponentService {
    readonly selectedPlan$: Observable<Plan | null>;
    readonly selectedLearningOpportunityName$: Observable<LocalizedString | null>;
    readonly options$: Observable<readonly PlanSelectOption[]>;
    readonly selectedPlanEducationName$: Observable<LocalizedString | null>;
    readonly selectedPlanCurriculumPeriodName$: Observable<LocalizedString | null>;

    constructor(
        private readonly _stateService: StateService,
        private readonly _uiRouterGlobals: UIRouterGlobals,
        planSelectService: PlanSelectService,
        @Inject(PLAN_SEEKER) private readonly _planSeeker: any,
        educationEntityService: EducationEntityService,
        appErrorHandler: AppErrorHandler,
        curriculumPeriodEntityService: CurriculumPeriodEntityService,
        myPlanService: MyPlanService,
    ) {
        const plans$: Observable<readonly Plan[]> = myPlanService.myPlans$.pipe(
            // Remove redundant sequential duplicates. There are no equal arrays except for empty ones.
            removeSequentialEmptyArrays(),
            appErrorHandler.defaultErrorHandler(),
            shareReplay(<ShareReplayConfig>{ bufferSize: 1, refCount: true }),
        );

        // prettier-ignore
        this.selectedPlan$ = of(null).pipe(
            concatMap(() => {
                // Selected id is not gonna change during this component's lifespan,
                // so we don't need to subscribe to plans$ unless there's an actual id.
                const selectedPlanId: OtmId | null = this.selectedPlanId;
                return selectedPlanId
                    ? plans$.pipe(
                        map(plans => plans.find(plan => plan.id === selectedPlanId) ?? null),
                    )
                    : of(null);
            }),
            // remove redundant sequential duplicates, most probably null
            distinctUntilChanged(),
            appErrorHandler.defaultErrorHandler(),
            shareReplay(<ShareReplayConfig>{ bufferSize: 1, refCount: true }),
        );

        const curriculumPeriodMap$: Observable<Map<OtmId, CurriculumPeriod>> = plans$.pipe(
            map(plans => new Set<OtmId>(plans.map(plan => plan.curriculumPeriodId).filter(Boolean))),
            distinctUntilChanged(setsAreEqual),
            switchMap(curriculumPeriodIds =>
                // prettier-ignore
                curriculumPeriodIds.size
                    ? curriculumPeriodEntityService.getByIds([...curriculumPeriodIds])
                    : of([]),
            ),
            // Remove redundant sequential duplicates. There are no equal arrays except for empty ones.
            removeSequentialEmptyArrays(),
            map(
                (planCurriculumPeriods: CurriculumPeriod[]) =>
                    new Map<OtmId, CurriculumPeriod>(
                        planCurriculumPeriods.map(curriculumPeriod => [curriculumPeriod.id, curriculumPeriod]),
                    ),
            ),
            appErrorHandler.defaultErrorHandler(),
            shareReplay(<ShareReplayConfig>{ bufferSize: 1, refCount: true }),
        );

        this.selectedPlanCurriculumPeriodName$ = this.selectedPlan$.pipe(
            map(selectedPlan => selectedPlan?.curriculumPeriodId ?? null),
            distinctUntilChanged(),
            switchMap((selectedPlanCurriculumPeriodId: OtmId | null) =>
                selectedPlanCurriculumPeriodId
                    ? curriculumPeriodMap$.pipe(
                          map(planCurriculumPeriodMap => planCurriculumPeriodMap.get(selectedPlanCurriculumPeriodId)?.name ?? null),
                      )
                    : of(null),
            ),
            // remove redundant sequential duplicates, most probably null
            distinctUntilChanged(),
            appErrorHandler.defaultErrorHandler(),
            shareReplay(<ShareReplayConfig>{ bufferSize: 1, refCount: true }),
        );

        const planEducations$: Observable<Education[]> = plans$.pipe(
            map(plans => new Set<OtmId>(plans.map(plan => plan.rootId).filter(Boolean))),
            distinctUntilChanged(setsAreEqual),
            switchMap(
                educationIds =>
                    educationIds.size
                        ? educationEntityService.getByIds([...educationIds]) //
                        : of([]), //
            ),
            // Remove redundant sequential duplicates. There are no equal arrays except for empty ones.
            removeSequentialEmptyArrays(),
            appErrorHandler.defaultErrorHandler(),
            shareReplay(<ShareReplayConfig>{ bufferSize: 1, refCount: true }),
        );

        const optionsWithoutCurriculumPeriodName$: Observable<PlanOption[]> = plans$.pipe(
            combineLatestWith(planEducations$),
            map(([plans, planEducations]) => planSelectService.createSelectOptionsFromPlansAndEducations(plans, planEducations)),
            // Remove redundant sequential duplicates. There are no equal arrays except for empty ones.
            removeSequentialEmptyArrays(),
            appErrorHandler.defaultErrorHandler(),
            shareReplay(<ShareReplayConfig>{ bufferSize: 1, refCount: true }),
        );

        // add curriculum period name to options
        this.options$ = optionsWithoutCurriculumPeriodName$.pipe(
            combineLatestWith(curriculumPeriodMap$),
            map(([optionsWithoutCurriculumPeriodName, curriculumPeriodMap]) =>
                optionsWithoutCurriculumPeriodName.map(option => {
                    const curriculumPeriod: CurriculumPeriod | undefined = option.plan?.curriculumPeriodId
                        ? curriculumPeriodMap.get(option.plan.curriculumPeriodId)
                        : undefined;
                    return <PlanSelectOption>{
                        ...option,
                        curriculumPeriodName: curriculumPeriod?.name,
                    };
                }),
            ),
            // Remove redundant sequential duplicates. There are no equal arrays except for empty ones.
            removeSequentialEmptyArrays(),
            appErrorHandler.defaultErrorHandler(),
            shareReplay(<ShareReplayConfig>{ bufferSize: 1, refCount: true }),
        );

        this.selectedLearningOpportunityName$ = this.selectedPlan$.pipe(
            map(selectedPlan => selectedPlan?.id ?? null),
            distinctUntilChanged(),
            switchMap(selectedPlanId =>
                selectedPlanId
                    ? optionsWithoutCurriculumPeriodName$.pipe(
                          map(
                              optionsWithoutCurriculumPeriodName =>
                                  optionsWithoutCurriculumPeriodName.find(option => !!option.plan && option.plan.id === selectedPlanId)
                                      ?.selectedLearningOpportunityName ?? null,
                          ),
                      )
                    : of(null),
            ),
            // remove redundant sequential duplicates, most probably null
            distinctUntilChanged(),
            appErrorHandler.defaultErrorHandler(),
            shareReplay(<ShareReplayConfig>{ bufferSize: 1, refCount: true }),
        );

        this.selectedPlanEducationName$ = this.selectedPlan$.pipe(
            map(selectedPlan => selectedPlan?.rootId ?? null),
            distinctUntilChanged(),
            switchMap((selectedPlanRootId: OtmId | null) =>
                selectedPlanRootId
                    ? planEducations$.pipe(
                          map(planEducations => planEducations.find(education => education.id === selectedPlanRootId)?.name ?? null),
                      )
                    : of(null),
            ),
            // remove redundant sequential duplicates, most probably null
            distinctUntilChanged(),
            appErrorHandler.defaultErrorHandler(),
            shareReplay(<ShareReplayConfig>{ bufferSize: 1, refCount: true }),
        );
    }

    onSelect(planId: OtmId): void {
        if (planId === this.selectedPlanId) {
            // prevent redundant reselect
            return;
        }

        this._planSeeker.storeSelectedPlanId(planId);
        this._stateService.go(this._uiRouterGlobals.current, { planId, selectedModuleId: null });
    }

    private get selectedPlanId(): OtmId | null {
        return this._uiRouterGlobals.params.planId ?? null;
    }
}

export interface PlanSelectOption extends PlanOption {
    readonly curriculumPeriodName: LocalizedString | null | undefined;
}

function removeSequentialEmptyArrays<T extends readonly any[]>(): MonoTypeOperatorFunction<T> {
    return (src: Observable<T>) => src.pipe(distinctUntilChanged((a, b) => !a.length && !b.length));
}
